/**
 * Condition runtime types and the static definition registry.
 *
 * `combat-physical` (B03) splits conditions into two flavours:
 *   - **status** — duration-based; counts down in seconds and expires.
 *   - **tick**   — damage-over-time; emits a damage event every `tickIntervalSec`
 *                  for `totalTicks` ticks, then expires.
 *
 * Stat-modifier hooks (the per-condition flat numeric modifiers from B04) are
 * not modelled here; B03 only owns the lifecycle (apply / tick / cleanse).
 */

import type { DamageType } from "./combat.js";
import type { ConditionId } from "./entity.js";

export type ConditionKind = "status" | "tick";

/** When a condition is automatically removed by an external system. */
export type CleanseTrigger =
  | "shortRest"
  | "longRest"
  | "combatEnd"
  /** Removed via lesser-restoration / cleanse spell or item. */
  | "restoration";

/**
 * How a re-application interacts with an existing instance:
 *   - `refresh` → reset the remaining duration / tick budget to the new value
 *     (Sleep, Charm).
 *   - `extend`  → add the new value to the existing remainder (Blessed, Poisoned).
 *   - `ignore`  → leave the existing instance untouched (immutable while live).
 */
export type StackPolicy = "refresh" | "extend" | "ignore";

/** Fixed metadata describing one condition family. */
export type ConditionDef =
  | {
      readonly id: ConditionId;
      readonly kind: "status";
      readonly stack: StackPolicy;
      readonly cleanseOn: readonly CleanseTrigger[];
      /** Default duration in seconds when none is supplied to `apply`. */
      readonly defaultDurationSec: number;
    }
  | {
      readonly id: ConditionId;
      readonly kind: "tick";
      readonly stack: StackPolicy;
      readonly cleanseOn: readonly CleanseTrigger[];
      /** Default total ticks when none is supplied to `apply`. */
      readonly defaultTicks: number;
      /** Seconds between ticks. */
      readonly tickIntervalSec: number;
      /** Damage type emitted each tick. */
      readonly tickDamageType: DamageType;
      /** Damage amount emitted each tick. */
      readonly tickDamageAmount: number;
    };

/** A live condition instance attached to a specific entity. */
export type ActiveCondition =
  | {
      readonly id: ConditionId;
      readonly kind: "status";
      remainingSec: number;
      readonly sourceId: string | null;
    }
  | {
      readonly id: ConditionId;
      readonly kind: "tick";
      remainingTicks: number;
      timeUntilNextTickSec: number;
      readonly sourceId: string | null;
    };

/** A damage emission produced by a tick condition; consumed by combat. */
export interface ConditionTickDamage {
  readonly targetId: string;
  readonly conditionId: ConditionId;
  readonly damageType: DamageType;
  readonly amount: number;
}

// ── Definition registry ──────────────────────────────────────────────────────

/**
 * Static catalogue for the v1 condition list (combat-physical.md). Numbers are
 * placeholders pending the refinement pass — the lifecycle does not depend on
 * them.
 */
export const CONDITION_DEFS: Readonly<Partial<Record<ConditionId, ConditionDef>>> = {
  // ── Status (duration in seconds) ───────────────────────────────────────────
  blessed: {
    id: "blessed",
    kind: "status",
    stack: "extend",
    cleanseOn: ["combatEnd", "restoration"],
    defaultDurationSec: 60,
  },
  haste: {
    id: "haste",
    kind: "status",
    stack: "refresh",
    cleanseOn: ["combatEnd"],
    defaultDurationSec: 30,
  },
  invisible: {
    id: "invisible",
    kind: "status",
    stack: "refresh",
    cleanseOn: ["combatEnd"],
    defaultDurationSec: 30,
  },
  strong: {
    id: "strong",
    kind: "status",
    stack: "extend",
    cleanseOn: ["combatEnd", "restoration"],
    defaultDurationSec: 60,
  },
  blinded: {
    id: "blinded",
    kind: "status",
    stack: "refresh",
    cleanseOn: ["restoration", "shortRest"],
    defaultDurationSec: 30,
  },
  charmed: {
    id: "charmed",
    kind: "status",
    stack: "refresh",
    cleanseOn: ["combatEnd", "restoration"],
    defaultDurationSec: 60,
  },
  silence: {
    id: "silence",
    kind: "status",
    stack: "refresh",
    cleanseOn: ["combatEnd"],
    defaultDurationSec: 30,
  },
  sleep: {
    id: "sleep",
    kind: "status",
    stack: "refresh",
    cleanseOn: ["restoration", "combatEnd"],
    defaultDurationSec: 60,
  },
  slow: {
    id: "slow",
    kind: "status",
    stack: "refresh",
    cleanseOn: ["combatEnd", "restoration"],
    defaultDurationSec: 30,
  },
  paralyzed: {
    id: "paralyzed",
    kind: "status",
    stack: "refresh",
    cleanseOn: ["combatEnd", "restoration"],
    defaultDurationSec: 10,
  },
  petrified: {
    id: "petrified",
    kind: "status",
    stack: "refresh",
    cleanseOn: ["restoration"],
    defaultDurationSec: 120,
  },

  // ── Tick (DoT) ─────────────────────────────────────────────────────────────
  burning: {
    id: "burning",
    kind: "tick",
    stack: "extend",
    cleanseOn: ["combatEnd"],
    defaultTicks: 3,
    tickIntervalSec: 2,
    tickDamageType: "fire",
    tickDamageAmount: 2,
  },
  poisoned: {
    id: "poisoned",
    kind: "tick",
    stack: "extend",
    cleanseOn: ["restoration", "shortRest"],
    defaultTicks: 4,
    tickIntervalSec: 3,
    tickDamageType: "poison",
    tickDamageAmount: 1,
  },
  disease: {
    id: "disease",
    kind: "tick",
    stack: "extend",
    cleanseOn: ["restoration", "longRest"],
    defaultTicks: 6,
    tickIntervalSec: 6,
    tickDamageType: "necrotic",
    tickDamageAmount: 1,
  },
  bleeding: {
    id: "bleeding",
    kind: "tick",
    stack: "extend",
    cleanseOn: ["shortRest", "restoration"],
    defaultTicks: 3,
    tickIntervalSec: 3,
    tickDamageType: "necrotic",
    tickDamageAmount: 1,
  },
};

/** Returns the registered definition or `null` if the id has no v1 entry. */
export function getConditionDef(id: ConditionId): ConditionDef | null {
  return CONDITION_DEFS[id] ?? null;
}
